/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { FiltroParecer, IPareceres, ISubfiltros, PageParecer } from '../types';
import ParecerService from '@/views/parecer/service/ParecerService';

import SdsSnackbarQueue from '@ungp/sds-snackbar-queue';
import { DataOptions } from 'vuetify';
import { isEqual } from 'lodash';
import {
  transformarDadosSubFilter,
  verificarSePossuiItensSelecionadosOuExpandidos,
} from '@/utils/transform';
import { cloneDeep } from '@apollo/client/utilities';
import moment from 'moment';

export const ConsultaParecerStore = defineStore('consultaParecerStore', () => {
  const pageParecer = ref<PageParecer>({});

  const pageSubfiltros = ref<ISubfiltros[]>([]);

  const pageSubfiltrosOld = ref<ISubfiltros[] | undefined>(undefined);

  const showFilter = ref(false);

  const dataOptions = ref({
    page: 1,
    itemsPerPage: 10,
    sortBy: [''],
    sortDesc: [true],
  } as DataOptions);

  const filtroParecer = ref<FiltroParecer>({
    expandirPesquisaAvancada: false,
    dePesquisaTermos: '',
    flEmenta: true,
    flParecer: true,
    nuProcesso: '',
    nuParecer: '',
    nuParecerPrefixo: '',
    anoNuParecer: '',
    nuParecerSufixo: '',
    dtParecerInicio: '',
    dtParecerFim: '',
    dtDecisaoInicio: '',
    dtDecisaoFim: '',
    categoriaSelected: null,
    categorias: [],
    autoresSelected: [],
    autores: [],
    corregedorSelected: [],
    corregedores: [],
    subFilter: {
      categorias: [],
      autores: [],
      corregedores: [],
    },
    numPag: 0,
    ordenacao: 'dtParecer DESC',
    subFiltrosOld: [],
    corregedor: [],
  });

  const pareceres = computed(() => pageParecer.value.pareceres || []);

  const pareceresSelecionados = ref<IPareceres[]>([]);

  const listaHashPareceresSelecionados = computed(() =>
    pareceresSelecionados.value.map(
      (item: IPareceres) => item.hashNuIdentificacao
    )
  );

  const subFiltrosCategorias = computed(() => {
    if (pageSubfiltros.value) {
      const resultadoFinal = transformarDadosSubFilter(pageSubfiltros.value);

      return resultadoFinal;
    }
    return {
      classes: [],
      relatores: [],
      comarcas: [],
      orgaosJulgadores: [],
      revistas: [],
      anos: [],
      prolatores: [],
    };
  });

  const totalHits = computed(() => pageParecer.value.totalHits || 0);

  const limiteTotalHists = 10000;

  const paginacao = computed(() => {
    const { page } = dataOptions.value;
    return page - 1;
  });

  const totalElementos = computed(() => {
    if (totalHits.value > limiteTotalHists) return limiteTotalHists;
    return totalHits.value;
  });

  const params = computed<FiltroParecer>(() => {
    const filtro = filtroParecer.value;
    const subFilter: any = {};
    pageSubfiltros.value.forEach((subFiltro) => {
      subFilter[subFiltro.campo] = subFiltro.valoresSubFiltroResponseDTO
        .filter((valores) => valores.checked)
        .map((valores) => valores.descricao);
    });

    const nuParecer =
      filtro.nuParecerPrefixo || filtro.anoNuParecer
        ? `${filtro.nuParecerPrefixo}/${filtro.anoNuParecer}-${filtro.nuParecerSufixo}`
        : '';
    return {
      dePesquisaTermos: filtro.dePesquisaTermos ?? '',
      flEmenta: filtro.flEmenta,
      flParecer: filtro.flParecer,
      nuProcesso: filtro.nuProcesso,
      nuParecer,
      nuParecerPrefixo: filtro.nuParecerPrefixo,
      anoNuParecer: filtro.anoNuParecer,
      nuParecerSufixo: filtro.nuParecerSufixo,
      dtParecerInicio: filtro.dtParecerInicio
        ? moment(filtro.dtParecerInicio).valueOf()
        : '',
      dtParecerFim: filtro.dtParecerFim
        ? moment(filtro.dtParecerFim).valueOf()
        : '',
      dtDecisaoInicio: filtro.dtDecisaoInicio
        ? moment(filtro.dtDecisaoInicio).valueOf()
        : '',
      dtDecisaoFim: filtro.dtDecisaoFim
        ? moment(filtro.dtDecisaoFim).valueOf()
        : '',
      categoriaSelected: filtro.categoriaSelected || {},
      categorias: filtro.categorias,
      autoresSelected: filtro.autoresSelected,
      autores: filtro.autores,
      corregedorSelected: filtro.corregedorSelected,
      corregedores: filtro.corregedores,
      subFilter: subFilter ?? {
        categorias: [],
        autores: [],
        corregedores: [],
      },
      numPag: paginacao.value,
      ordenacao: filtro.ordenacao,
      subFiltrosOld: filtro.subFiltrosOld?.length
        ? filtro.subFiltrosOld
        : undefined,
    };
  });

  const isPrivado = ref(false);

  const consultaListaParecer = async (initialPagination = false) => {
    pageParecer.value = {};
    const messageError = 'Não foi possível buscar os registros';
    try {
      if (initialPagination) {
        dataOptions.value.page = 1;
        pageSubfiltros.value = [];
        pageSubfiltrosOld.value = undefined;
        showFilter.value = false;
      }

      const response = isPrivado.value
        ? await ParecerService.listarParecerPrivado(params.value)
        : await ParecerService.consultarListaParecer(params.value);
      if (response.hasError) {
        SdsSnackbarQueue.error(response.error?.message || messageError);
        return;
      }

      pageSubfiltrosOld.value = cloneDeep(pageSubfiltros.value);
      pageParecer.value = response.content!;
      const subfiltrosAtualizados = pageParecer.value.subfiltros || undefined;
      if (pageSubfiltrosOld.value && pageSubfiltrosOld.value.length > 0) {
        pageSubfiltros.value = verificarSePossuiItensSelecionadosOuExpandidos(
          subfiltrosAtualizados,
          pageSubfiltrosOld.value
        );
      } else {
        pageSubfiltros.value = subfiltrosAtualizados!;
      }
    } catch (error) {
      SdsSnackbarQueue.error(messageError);
    }
  };

  const atualizarPaginacao = (refElemento: any, pagina: number) => {
    if (!isEqual(dataOptions, pagina)) {
      dataOptions.value.page = pagina;
      consultaListaParecer();
      refElemento.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const atualizarOrdenacao = (ordenacao: { value: string }) => {
    filtroParecer.value.ordenacao = ordenacao.value;
    consultaListaParecer();
  };

  const limparTodosFiltroParecer = () => {
    pageParecer.value = {};

    pageSubfiltros.value = [];

    pageSubfiltrosOld.value = undefined;

    dataOptions.value = {
      page: 1,
      itemsPerPage: 10,
      sortBy: [''],
      sortDesc: [true],
    } as DataOptions;

    filtroParecer.value = {
      expandirPesquisaAvancada: false,
      dePesquisaTermos: '',
      flEmenta: true,
      flParecer: true,
      nuProcesso: '',
      nuParecer: '',
      nuParecerPrefixo: '',
      anoNuParecer: '',
      nuParecerSufixo: '',
      dtParecerInicio: '',
      dtParecerFim: '',
      dtDecisaoInicio: '',
      dtDecisaoFim: '',
      categoriaSelected: null,
      categorias: [],
      autoresSelected: [],
      autores: [],
      corregedorSelected: [],
      corregedores: [],
      subFilter: {
        categorias: [],
        autores: [],
        corregedores: [],
      },
      numPag: 0,
      ordenacao: 'dtParecer DESC',
      subFiltrosOld: [],
      corregedor: [],
    };
  };

  const downloadSelecionados = () => {
    download(listaHashPareceresSelecionados.value);
  };

  const download = (listHashNuIdentificacao: string[]) => {
    listHashNuIdentificacao.forEach((hashNuIdentificacao: string) => {
      const url = `/gecon/parecer/visualizar/${hashNuIdentificacao}`;
      window.open(url, '_blank');
    });
  };

  return {
    pageParecer,
    pageSubfiltros,
    pageSubfiltrosOld,
    dataOptions,
    paginacao,
    totalHits,
    pareceres,
    pareceresSelecionados,
    totalElementos,
    filtroParecer,
    subFiltrosCategorias,
    showFilter,
    isPrivado,
    downloadSelecionados,
    consultaListaParecer,
    limparTodosFiltroParecer,
    atualizarPaginacao,
    atualizarOrdenacao,
    download,
  };
});
